import {
    create,
    STANDARD_LANDSCAPE,
    STANDARD_PORTRAIT
} from 'amazon-ivs-web-broadcast';

/**
 * Live Streaming Service - Production Mode with AWS IVS
 * 
 * This service uses the Amazon IVS Web Broadcast SDK for production-ready live streaming.
 * 
 * Features:
 * - Direct WebRTC streaming to AWS IVS
 * - Built-in adaptive bitrate streaming
 * - Automatic quality adjustments
 * - Professional broadcasting capabilities
 * - Real-time stream monitoring
 * 
 * Requirements:
 * - Valid AWS IVS stream configuration (ingest endpoint + stream key)
 * - HTTPS connection (required for WebRTC)
 * - Camera and microphone permissions
 */

class LiveStreamingService {
    constructor() {
        this.broadcastClient = null;
        this.stream = null;
        this.isStreaming = false;
        this.streamConfig = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000;
    }

    /**
     * Initialize streaming with AWS IVS configuration
     * @param {Object} config - Stream configuration from API response
     * @param {MediaStream} mediaStream - Camera/microphone stream
     */
    async initializeStream(config, mediaStream) {
        try {
            this.streamConfig = config;
            this.stream = mediaStream;

            // Create AWS IVS Broadcast Client with basic configuration
            this.broadcastClient = create({
                streamConfig: STANDARD_LANDSCAPE,
            });

            // Set up event listeners
            this.setupBroadcastEventListeners();

            // Add media stream to broadcast client
            await this.addMediaStreams(mediaStream);

            return true;

        } catch (error) {
            console.error('Failed to initialize AWS IVS streaming:', error);
            throw new Error(`Failed to initialize streaming service: ${error.message}`);
        }
    }

    /**
     * Set up AWS IVS Broadcast Client event listeners
     */
    setupBroadcastEventListeners() {
        if (!this.broadcastClient) return;

        // Helper to register event handlers in a way that supports different
        // client implementations. Some versions of the IVS SDK expose
        // `addEventListener`, others expose `on` or `addListener`. Fall back
        // to a no-op if none are present to avoid runtime TypeErrors.
        const addHandler = (eventName, handler) => {
            if (!this.broadcastClient) return;
            if (typeof this.broadcastClient.addEventListener === 'function') {
                this.broadcastClient.addEventListener(eventName, handler);
            } else if (typeof this.broadcastClient.on === 'function') {
                this.broadcastClient.on(eventName, handler);
            } else if (typeof this.broadcastClient.addListener === 'function') {
                this.broadcastClient.addListener(eventName, handler);
            } else {
                // last-resort: if the client exposes an events map, try to push
                // into it; otherwise just log that event registration isn't
                // supported in this environment.
                if (this.broadcastClient.events && Array.isArray(this.broadcastClient.events[eventName])) {
                    this.broadcastClient.events[eventName].push(handler);
                } else {
                    console.warn(`Broadcast client does not support event registration for ${eventName}`);
                }
            }
        };

        addHandler('error', (error) => {
            console.error('AWS IVS Broadcast error:', error);
            this.handleBroadcastError(error);
        });

        addHandler('connectionStateChanged', (state) => {
            
            if (state === 'disconnected' && this.isStreaming) {
                this.handleConnectionLoss();
            }
        });

        addHandler('streamStateChanged', (state) => {
        });
    }

    /**
     * Add media streams to the broadcast client
     */
    async addMediaStreams(mediaStream) {
        if (!this.broadcastClient || !mediaStream) {
            throw new Error('Broadcast client or media stream not available');
        }

        try {
            // Get video and audio tracks
            const videoTracks = mediaStream.getVideoTracks();
            const audioTracks = mediaStream.getAudioTracks();

            // Add video stream if available
            if (videoTracks.length > 0) {
                // Create a video-only MediaStream
                const videoStream = new MediaStream([videoTracks[0]]);
                await this.broadcastClient.addVideoInputDevice(videoStream, 'camera', {
                    index: 0,
                    x: 0,
                    y: 0,
                    width: 1920,
                    height: 1080
                });
            }

            // Add audio stream if available
            if (audioTracks.length > 0) {
                // Create an audio-only MediaStream
                const audioStream = new MediaStream([audioTracks[0]]);
                await this.broadcastClient.addAudioInputDevice(audioStream, 'microphone');
            }

        } catch (error) {
            console.error('Error adding media streams:', error);
            throw error;
        }
    }

    /**
     * Start the live stream
     */
    async startStreaming() {
        if (!this.broadcastClient || !this.streamConfig) {
            throw new Error('Stream not initialized');
        }

        try {
            // Validate stream configuration
            if (!this.streamConfig.ingest_endpoint || !this.streamConfig.stream_key) {
                throw new Error('Invalid stream configuration: missing ingest endpoint or stream key');
            }

            // Start broadcasting to AWS IVS
            await this.broadcastClient.startBroadcast(
                this.streamConfig.stream_key,
                this.streamConfig.ingest_endpoint
            );

            this.isStreaming = true;
            this.reconnectAttempts = 0;

            
            return true;
        } catch (error) {
            console.error('Failed to start AWS IVS streaming:', error);
            throw new Error(`Failed to start streaming: ${error.message}`);
        }
    }

    /**
     * Stop the live stream
     */
    async stopStreaming() {
        if (!this.isStreaming) {
            return;
        }

        try {
            if (this.broadcastClient) {
                await this.broadcastClient.stopBroadcast();
            }

            this.isStreaming = false;
            this.reconnectAttempts = 0;

        } catch (error) {
            console.error('Error stopping AWS IVS stream:', error);
            // Still mark as stopped even if there was an error
            this.isStreaming = false;
            throw error;
        }
    }

    /**
     * Handle broadcast errors
     */
    handleBroadcastError(error) {
        console.error('Handling broadcast error:', error);
        
        // Check if it's a recoverable error
        if (error.code === 'CONNECTION_LOST' && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.attemptReconnect();
        } else {
            // Stop streaming on unrecoverable errors
            this.isStreaming = false;
        }
    }

    /**
     * Handle connection loss
     */
    handleConnectionLoss() {
        console.warn('Connection lost, attempting to reconnect...');
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.attemptReconnect();
        } else {
            console.error('Max reconnection attempts reached');
            this.isStreaming = false;
        }
    }

    /**
     * Attempt to reconnect to streaming server
     */
    async attemptReconnect() {
        this.reconnectAttempts++;

        setTimeout(async () => {
            try {
                if (this.broadcastClient && this.streamConfig) {
                    await this.broadcastClient.startBroadcast(
                        this.streamConfig.stream_key,
                        this.streamConfig.ingest_endpoint
                    );
                    this.reconnectAttempts = 0;
                }
            } catch (error) {
                console.error('Reconnection failed:', error);
                
                if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                    console.error('Max reconnection attempts reached. Stopping stream.');
                    this.isStreaming = false;
                }
            }
        }, this.reconnectDelay * this.reconnectAttempts);
    }

    /**
     * Get current stream status
     */
    getStreamStatus() {
        return {
            isStreaming: this.isStreaming,
            isConnected: this.broadcastClient?.getSessionState ? 
                this.broadcastClient.getSessionState() === 'connected' : 
                this.isStreaming,
            config: this.streamConfig,
            reconnectAttempts: this.reconnectAttempts,
            mode: 'production', // AWS IVS production mode
            sdk: 'amazon-ivs-web-broadcast'
        };
    }

    /**
     * Update stream quality
     */
    async updateStreamQuality(bitrate) {
        if (this.broadcastClient && this.isStreaming) {
            try {
                // AWS IVS supports dynamic quality adjustments
                await this.broadcastClient.updateVideoConfiguration({
                    maxBitrate: bitrate
                });
            } catch (error) {
                console.error('Failed to update stream quality:', error);
            }
        }
    }

    /**
     * Get stream statistics
     */
    getStreamStats() {
        if (this.broadcastClient) {
            try {
                return this.broadcastClient.getStats();
            } catch (error) {
                console.error('Failed to get stream stats:', error);
                return null;
            }
        }
        return null;
    }

    /**
     * Dispose of the streaming service
     */
    dispose() {
        try {
            if (this.isStreaming) {
                this.stopStreaming();
            }
            
            if (this.broadcastClient) {
                this.broadcastClient.delete();
                this.broadcastClient = null;
            }
            
            this.stream = null;
            this.streamConfig = null;
            
        } catch (error) {
            console.error('Error disposing streaming service:', error);
        }
    }
}

// Singleton instance
const liveStreamingService = new LiveStreamingService();

export default liveStreamingService;